"use client";

import { type CSSProperties, Fragment, useCallback, useState } from "react";

import Image from "next/image";

import cn from "classnames";

import { Accordion } from "@/components/accordion/accordion.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { FaqSection as FaqSectionType, Theme } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

export const FaqAccordionList = ({
  groups,
  theme,
  isCenteredLayout,
  isMonitoringPage,
}: FaqAccordionListProps) => {
  const [expandedFaqKey, setExpandedFaqKey] = useState<string | null>(null);

  const handleAccordionToggle = useCallback((key: string, isOpen: boolean) => {
    setExpandedFaqKey((currentKey) => {
      if (isOpen) return key;
      if (currentKey === key) return null;
      return currentKey;
    });
  }, []);

  return groups.map((group) => (
    <Fragment key={group.key}>
      {group.heading ? (
        <Text
          variant="h5"
          component={isCenteredLayout ? "h3" : "h4"}
          className={cn("mb-2 not-first:mt-6 lg:not-first:mt-20", {
            "mx-6": !isCenteredLayout,
            "text-greyscale-headline": isMonitoringPage,
          })}>
          {group.heading}
        </Text>
      ) : null}
      {group.faqs.map((faq) => {
        const faqKey = `${group.key}:${faq._key}`;
        return (
          <FaqAccordion
            key={faq._key}
            faq={faq}
            theme={theme}
            isCenteredLayout={isCenteredLayout}
            isMonitoringPage={isMonitoringPage}
            isExpanded={expandedFaqKey === faqKey}
            onToggle={(isOpen) => handleAccordionToggle(faqKey, isOpen)}
          />
        );
      })}
    </Fragment>
  ));
};

const FaqAccordion = ({
  faq,
  theme,
  isCenteredLayout,
  isMonitoringPage,
  isExpanded,
  onToggle,
}: FaqAccordionProps) => (
  <Accordion
    title={faq.question}
    titleVariant={isCenteredLayout ? "body3" : "h6"}
    isExpanded={isExpanded}
    isCollapsible={true}
    theme={theme}
    onToggle={onToggle}
    arrowSize={isCenteredLayout ? 16 : 24}
    flush={isCenteredLayout}
    summaryClassName={cn({
      "py-2 group-not-first:py-2": isCenteredLayout,
      "!text-greyscale-headline": isMonitoringPage,
    })}
    contentClassName={cn({
      "pb-5 text-greyscale-body": isCenteredLayout,
    })}>
    <FaqAnswer faq={faq} />
  </Accordion>
);

const DEFAULT_IMAGE_SIZE = {
  mobile: { width: 335, height: 265 },
  desktop: { width: 213, height: 265 },
};

const DEFAULT_IMAGE_GAP = { mobile: "16", desktop: "24" };

const IMAGE_ALIGN_SELF = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
};

const FaqAnswer = ({ faq }: { faq: FaqItem }) => {
  const answer = (
    <>
      <RichText>{faq.answer}</RichText>
      {faq.legalDisclaimer?.length ? (
        <div className="mt-4">
          <RichText>{faq.legalDisclaimer}</RichText>
        </div>
      ) : null}
    </>
  );

  if (!faq.image?.asset) return answer;

  const mobileWidth =
    faq.mobileImageSize?.width ?? DEFAULT_IMAGE_SIZE.mobile.width;
  const mobileHeight =
    faq.mobileImageSize?.height ?? DEFAULT_IMAGE_SIZE.mobile.height;
  const desktopWidth = faq.imageSize?.width ?? DEFAULT_IMAGE_SIZE.desktop.width;
  const desktopHeight =
    faq.imageSize?.height ?? DEFAULT_IMAGE_SIZE.desktop.height;
  const { hotspot } = faq.image;

  const image = (
    <Image
      src={urlFor(faq.image)
        .width(Math.max(mobileWidth, desktopWidth) * 2)
        .url()}
      alt={faq.image.alt ?? ""}
      width={mobileWidth}
      height={mobileHeight}
      className="h-(--faq-image-height) w-(--faq-image-width) max-w-full shrink-0 rounded-lg object-cover md:h-(--md-faq-image-height) md:w-(--md-faq-image-width) md:[align-self:var(--md-faq-image-align)]"
      style={
        {
          "--faq-image-width": `${mobileWidth}px`,
          "--faq-image-height": `${mobileHeight}px`,
          "--md-faq-image-width": `${desktopWidth}px`,
          "--md-faq-image-height": `${desktopHeight}px`,
          "--md-faq-image-align": IMAGE_ALIGN_SELF[faq.imageAlignment ?? "top"],
          objectPosition: hotspot
            ? `${hotspot.x * 100}% ${hotspot.y * 100}%`
            : undefined,
        } as CSSProperties
      }
    />
  );
  const isImageLeft = faq.imagePosition === "left";

  return (
    <div
      className="flex flex-col gap-(--faq-image-gap) md:flex-row md:items-start md:gap-(--md-faq-image-gap)"
      style={
        {
          "--faq-image-gap": `${faq.mobileImageGap ?? DEFAULT_IMAGE_GAP.mobile}px`,
          "--md-faq-image-gap": `${faq.imageGap ?? DEFAULT_IMAGE_GAP.desktop}px`,
        } as CSSProperties
      }>
      {isImageLeft ? image : null}
      <div className="min-w-0 flex-1">{answer}</div>
      {isImageLeft ? null : image}
    </div>
  );
};

type FaqItem = NonNullable<FaqSectionType["faqs"]>[number];

export type FaqList = {
  key: string;
  heading?: string;
  faqs: FaqItem[];
};

type FaqAccordionListProps = {
  groups: FaqList[];
  theme?: Theme;
  isCenteredLayout: boolean;
  isMonitoringPage: boolean;
};

type FaqAccordionProps = {
  faq: FaqItem;
  theme?: Theme;
  isCenteredLayout: boolean;
  isMonitoringPage: boolean;
  isExpanded: boolean;
  onToggle: (isOpen: boolean) => void;
};
